import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/core/stores/auth'
import { useIsAdmin } from '@/core/components/RequireRole'
import { TremorLogo } from '@/shared/components/ui/TremorLogo'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import {
  Radar, Search, Star, TrendingUp, ChevronRight, Camera,
  Sparkles, Zap, Shield, BarChart3, Globe, ImageIcon,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import api from '@/shared/lib/api'

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Default cover gradient when no image is set ──
const DEFAULT_COVER = 'linear-gradient(135deg, #232018 0%, #2D2920 40%, #0F7173 70%, #C9A84C 100%)'

// ── Changelog entries — hardcoded for now, could come from API later ──
const changelog = [
  {
    version: '1.2', date: 'Mars 2026', tag: 'Nouveau', tagColor: 'bg-brick text-white',
    title: 'Search Demand Intelligence',
    description: 'Nouveau module SDI : analysez la demande marché, simulez vos budgets et identifiez les opportunités.',
    icon: TrendingUp, iconColor: '#0F7173',
  },
  {
    version: '1.1', date: 'Février 2026', tag: 'Amélioration', tagColor: 'bg-gold text-onyx',
    title: 'Gestion d\'équipe & rôles',
    description: 'Invitez des collaborateurs, gérez les rôles (admin, manager, viewer) et contrôlez les accès.',
    icon: Shield, iconColor: '#C9A84C',
  },
  {
    version: '1.0', date: 'Janvier 2026', tag: 'Lancement', tagColor: 'bg-onyx text-paper',
    title: 'TremorApp v1.0',
    description: 'Lancement avec Local Lead Radar, Google Footprint Optimizer et Auto Reputation Engine.',
    icon: Zap, iconColor: '#E8562A',
  },
]

const moduleCards = [
  { id: 'llr', shortLabel: 'LLR', label: 'Local Lead Radar', icon: Radar, color: '#C9A84C', path: '/llr', desc: 'Demande locale prédictive' },
  { id: 'gfo', shortLabel: 'GFO', label: 'Google Footprint', icon: Search, color: '#7B61FF', path: '/gfo', desc: 'SEO & avis Google' },
  { id: 'are', shortLabel: 'ARE', label: 'Auto Reputation', icon: Star, color: '#E8562A', path: '/are', desc: 'Croissance d\'avis' },
  { id: 'sdi', shortLabel: 'SDI', label: 'Search Demand Intel', icon: TrendingUp, color: '#0F7173', path: '/sdi', desc: 'Intelligence marché' },
]

// ── Cover Photo Upload (admin only) ──
function CoverUploadButton({ onUpload }: { onUpload: (file: File) => void }) {
  return (
    <label className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-onyx/70 text-paper text-[11px] font-medium cursor-pointer hover:bg-onyx/90 transition-colors backdrop-blur-sm">
      <Camera size={13} />
      Changer la couverture
      <input type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f) }} />
    </label>
  )
}

// ── Profile Photo Upload ──
function ProfilePhoto({ url, name, canEdit, onUpload }: {
  url?: string | null; name: string; canEdit?: boolean; onUpload?: (file: File) => void
}) {
  const initial = (name || '?')[0].toUpperCase()
  return (
    <div className="relative group">
      {url ? (
        <img src={url} alt={name} className="w-20 h-20 rounded-full object-cover border-4 border-paper shadow-elevated" />
      ) : (
        <div className="w-20 h-20 rounded-full bg-gold text-onyx text-2xl font-serif font-bold flex items-center justify-center border-4 border-paper shadow-elevated">
          {initial}
        </div>
      )}
      {canEdit && onUpload && (
        <label className="absolute inset-0 rounded-full bg-onyx/0 group-hover:bg-onyx/50 flex items-center justify-center cursor-pointer transition-colors">
          <Camera size={18} className="text-paper opacity-0 group-hover:opacity-100 transition-opacity" />
          <input type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f) }} />
        </label>
      )}
    </div>
  )
}

export function HomePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAdmin = useIsAdmin()
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir'
  const firstName = user?.full_name?.split(' ')[0] || ''

  // Cover photo state — would come from tenant settings in production
  const [coverUrl, setCoverUrl] = useState<string | null>(null)

  const handleCoverUpload = async (file: File) => {
    // TODO: Upload to API → PATCH /settings/workspace with cover_image
    // For now, create a local preview
    const url = URL.createObjectURL(file)
    setCoverUrl(url)
  }

  const handleProfileUpload = async (file: File) => {
    // TODO: Upload to API → PATCH /settings/profile with avatar
    // For now, just log
    console.log('Profile upload:', file.name)
  }

  return (
    <div className="space-y-6 -mt-6 -mx-4 sm:-mx-6 lg:-mx-8">
      {/* ═══ Cover + Profile Banner ═══ */}
      <div className="relative">
        {/* Cover image or gradient */}
        <div className="h-[200px] w-full rounded-b-2xl overflow-hidden relative"
          style={{
            background: coverUrl ? `url(${coverUrl}) center/cover no-repeat` : DEFAULT_COVER,
          }}>
          {/* Overlay gradient for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-onyx/60 via-transparent to-transparent" />

          {/* Admin: change cover button */}
          {isAdmin && <CoverUploadButton onUpload={handleCoverUpload} />}

          {/* Logo watermark bottom-left */}
          <div className="absolute bottom-4 left-6 flex items-center gap-2 opacity-40">
            <TremorLogo size={18} className="text-paper" />
            <span className="font-serif text-sm font-bold text-paper tracking-tight">tremorapp</span>
          </div>
        </div>

        {/* Profile section — overlaps cover with frosted glass */}
        <div className="relative px-4 sm:px-6 lg:px-8 -mt-14">
          <div className="inline-flex bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl shadow-elevated px-6 py-4">
            <div className="flex items-center gap-5">
              <ProfilePhoto
                url={user?.avatar_url}
                name={user?.full_name || user?.email || ''}
                canEdit={true}
                onUpload={handleProfileUpload}
              />
              <div className="flex-1 min-w-0">
                <h1 className="font-serif text-[26px] font-bold text-ink tracking-tight leading-tight">
                  {greeting}{firstName ? ', ' : ''}<span className="text-gold">{firstName}</span>
                </h1>
                <p className="text-[12px] font-light text-ink-muted mt-1">
                  {user?.email} · <span className="capitalize">{user?.role}</span>
                </p>
              </div>
            </div>
          </div>
          <div className="divider-gold mt-5" />
        </div>
      </div>

      {/* ═══ Main Content — 2 columns ═══ */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── LEFT: Dashboard + Actions (2/3) ── */}
          <div className="lg:col-span-2 space-y-6">
            {/* Module Overview Cards */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={14} className="text-ink-faint" />
                <h2 className="font-serif text-sm font-semibold text-ink">Tableau de bord</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {moduleCards.map((mod) => (
                  <button key={mod.id} onClick={() => navigate(mod.path)}
                    className="relative bg-white rounded-card border border-ink-border p-5 text-left shadow-card hover:shadow-elevated hover:-translate-y-px transition-all group overflow-hidden">
                    {/* Glow top bar */}
                    <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: mod.color }} />
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${mod.color}15` }}>
                        <mod.icon size={16} style={{ color: mod.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-ink">{mod.shortLabel}</p>
                      </div>
                      <ChevronRight size={13} className="text-ink-faint group-hover:text-ink transition-colors" />
                    </div>
                    <p className="text-[11px] font-light text-ink-muted">{mod.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="font-serif text-sm font-semibold text-ink mb-3">Actions rapides</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Nouveau radar', icon: Radar, path: '/llr/radars/new', color: '#C9A84C' },
                  { label: 'Nouvelle campagne', icon: Star, path: '/are/campaigns/new', color: '#E8562A' },
                  { label: 'Nouveau marché', icon: Globe, path: '/sdi/markets', color: '#0F7173' },
                ].map((a) => (
                  <button key={a.label} onClick={() => navigate(a.path)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ink-border bg-white text-[12px] font-medium text-ink hover:bg-gold-light/30 hover:border-gold/30 transition-all">
                    <a.icon size={14} style={{ color: a.color }} />
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT: News / Blog (1/3) ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-gold" />
              <h2 className="font-serif text-sm font-semibold text-ink">Nouveautés TremorApp</h2>
            </div>

            <div className="space-y-3">
              {changelog.map((entry, idx) => (
                <div key={idx} className="bg-white rounded-card border border-ink-border p-4 shadow-card hover:shadow-elevated transition-shadow">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${entry.iconColor}12` }}>
                      <entry.icon size={14} style={{ color: entry.iconColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={cn('text-[8px] font-bold px-1.5 py-0.5 rounded-full', entry.tagColor)}>{entry.tag}</span>
                        <span className="text-[9px] text-ink-faint">v{entry.version} · {entry.date}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-serif text-[13px] font-bold text-ink tracking-tight mb-1">{entry.title}</h3>
                  <p className="text-[11px] font-light text-ink-muted leading-relaxed">{entry.description}</p>
                </div>
              ))}

              {/* Placeholder for future API-driven content */}
              <div className="bg-paper-warm rounded-card border border-ink-border/50 border-dashed p-6 text-center">
                <ImageIcon size={24} className="mx-auto text-ink-faint/40 mb-2" />
                <p className="text-[11px] text-ink-faint">D'autres actualités apparaîtront ici.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-8 mt-4">
          <p className="text-[10px] text-ink-faint tracking-wide">
            TremorApp · Intelligence Marché · v1.2 · Mars 2026
          </p>
        </div>
      </div>
    </div>
  )
}
